import { isEmpty } from "lodash";
import { useContext, useState, useEffect, useCallback, useRef } from "react";
import { StoreContext } from "../Store/Store";
import { Actions } from "../Store/Reducer";
import { useAudio } from "../Audio/AudioProvider";
import MqttContext from "./MqttContext";
import { loadControlBoxesAndSettings } from "../../util/ApiUtils";
import { AudioAlertEvents, AudioRepositionEvents } from "../../util/Constants";
import mqtt from "mqtt";

/* Box events structure
{
  $boxId: {
    major: {
      eCode: "100104",
      eTime: 1655190174569,
      patientSN: "p00000000004",
      boxId: "bid-0a",
      bedNo: "bed00a",
    },
    timing: {
      $timingEventCode1: {
        mVal: {
          value:
            "010203040500000...",
        },
        eCode: "200502",
        eTime: 1654843929149,
        patientSN: "p00000000004",
        boxId: "bid-04",
      },
      $timingEventCode2: {
        mVal: { value: "ffffff..." },
        eCode: "200501",
        eTime: 1654843929149,
        patientSN: "p00000000004",
        boxId: "bid-04",
      },
    },
    generationSetting: {
      {
        "boxId": "1B0063",
        "deploymentId": "erp01",
        "careUnitId": "cu-01",
        "bedNo": "b_00063",
        "patientSN": "p0001",
        "volume": "Off",
        ...
      }
    }
  },
}; */

let mqttClient;
const MqttProvider = (props) => {
  const [store, dispatch] = useContext(StoreContext);
  const [event, setEvent] = useState({});
  const [newGenerationSetting, setnewGenerationSetting] = useState(null);

  const eventTypes = { major: "major", timing: "timing" };
  const shadowTypes = { generationSettings: "generationSettings" };
  const { topicEvent, topicShadow } = store.vars.Mqtt.Subscription;
  const { ...options } = store.vars.Mqtt.Connection;
  const { host, port, user, paswd } = sessionStorage.getItem("mqttInfo")
    ? JSON.parse(sessionStorage.getItem("mqttInfo"))
    : {};
  const connectUrl = `wss://${host}:${port}/mqtt`;

  const { playLeavingAudio, playRepositionAudio } = useAudio();

  // 1. 使用 ref 追蹤最新的 event state 與暫存待處理的 Major 事件
  const eventRef = useRef(event);
  useEffect(() => {
    eventRef.current = event;
  }, [event]);

  // 暫存佇列：結構為 { [boxId]: { event: newEvent, timeoutId: number } }
  const pendingEventsRef = useRef({});

  // Connect or disconnect mqtt when user is logged in/out.
  useEffect(() => {
    if (!isEmpty(mqttClient)) {
      mqttClient.end();
      mqttClient = null;
    }
    if (!isEmpty(store.user) && !isEmpty(store.scStation)) {
      (async () => {
        await loadControlBoxesAndSettings({ store, dispatch });
        mqttConnect();
      })();
    }
  }, [store.user, store.scStation]);

  // Reload boxes when boxId in box list is different from generation setting (due to patient checked in or out)
  useEffect(() => {
    if (store.controlBoxes) {
      store.controlBoxes.forEach((box) => {
        if (box?.boxId === newGenerationSetting?.boxId) {
          if (box.patientSN !== newGenerationSetting.patientSN) {
            loadControlBoxesAndSettings({ store, dispatch });
          }
        }
      });
    }
  }, [newGenerationSetting]);

  function delay(time) {
    return new Promise((resolve) => setTimeout(resolve, time));
  }

  const [storedEvent, setStoredEvent] = useState({});
  const [autoReload, setAutoReload] = useState(null);
  useEffect(() => {
    (async () => {
      setStoredEvent(event);
      if (Object.keys(storedEvent).length === 0) await delay(200);
      else await delay(800);
      setAutoReload(Date.now());
    })();
  }, [autoReload]);

  // Clear events and boxes when care unit changed
  useEffect(() => {
    setEvent({});
    setStoredEvent({});
    pendingEventsRef.current = {}; // 清空佇列
    setAutoReload(Date.now());
    dispatch({ type: Actions.SetControlBoxes, payload: [] });
  }, [store.scStation]);

  const [boxes, setBoxes] = useState(store.controlBoxes);
  const boxesRef = useRef(boxes);

  useEffect(() => {
    setBoxes(store.controlBoxes);
  }, [store.controlBoxes]);

  useEffect(() => {
    boxesRef.current = boxes;
  }, [boxes]);

  // 檢查並執行音效播放 logic
  const triggerAudioIfEnabled = useCallback(
    (newEvent, generationSetting) => {
      const eventCode = newEvent.eCode;
      const belongLevingAudio = AudioAlertEvents.includes(eventCode);
      const belongRepositionAudio = AudioRepositionEvents.includes(eventCode);

      if (
        newEvent.patientSN &&
        newEvent.bedNo &&
        (belongLevingAudio || belongRepositionAudio)
      ) {
        const theBox = boxesRef.current.find(
          (box) => box.boxId === newEvent.boxId,
        );

        const isAlertsOn = generationSetting?.alertsOn === true;

        if (
          theBox &&
          theBox.patientSn === newEvent.patientSN &&
          theBox.bedNo === newEvent.bedNo &&
          isAlertsOn
        ) {
          if (belongLevingAudio) {
            playLeavingAudio();
          } else if (belongRepositionAudio) {
            playRepositionAudio();
          }
        }
      }
    },
    [playLeavingAudio, playRepositionAudio],
  );

  const processAudio = useCallback(
    (topic, message) => {
      try {
        let parsedMessage = JSON.parse(message);

        // A. 處理 Major Event 訊息
        if (topic.includes(eventTypes.major)) {
          const boxId = parsedMessage.boxId;
          const currentSettings = eventRef.current[boxId]?.generationSettings;

          // 若已有 settings 則直接判斷播放
          if (currentSettings && Object.keys(currentSettings).length > 0) {
            triggerAudioIfEnabled(parsedMessage, currentSettings);
          } else {
            // 若尚未取得 settings，放入暫存佇列並等待 generationSetting 到達
            if (pendingEventsRef.current[boxId]?.timeoutId) {
              clearTimeout(pendingEventsRef.current[boxId].timeoutId);
            }

            // 設定 Timeout (預設 3 秒)，若時限內仍未收到 settings，可作預設處理或捨棄
            const timeoutId = setTimeout(() => {
              if (pendingEventsRef.current[boxId]) {
                delete pendingEventsRef.current[boxId];
              }
            }, 3000);

            pendingEventsRef.current[boxId] = {
              event: parsedMessage,
              timeoutId,
            };
          }
        }

        // B. 處理 GenerationSettings Shadow 訊息
        else if (topic.includes(shadowTypes.generationSettings)) {
          const boxId = parsedMessage.boxId;
          const pendingItem = pendingEventsRef.current[boxId];

          // 收到 setting 後，若有積壓的 major event，取出補判斷並播放
          if (pendingItem) {
            clearTimeout(pendingItem.timeoutId);
            triggerAudioIfEnabled(pendingItem.event, parsedMessage);
            delete pendingEventsRef.current[boxId];
          }
        }
      } catch (error) {
        // ignore malformed
      }
    },
    [triggerAudioIfEnabled],
  );

  return (
    <MqttContext.Provider value={storedEvent}>
      {props.children}
    </MqttContext.Provider>
  );

  function mqttConnect() {
    options.username = user;
    options.password = paswd;

    mqttClient = mqtt.connect(connectUrl, options);
    mqttClient.on("connect", () => {
      console.log("MQTT connected!");
    });
    mqttClient.on("close", () => {
      console.log("MQTT disconnected!");
    });
    mqttClient.on("error", (error) => {
      console.log("MQTT connect failed!", error);
    });
    mqttClient.on("message", (topic, message) => {
      setMqttEvents(topic, message);
      processAudio(topic, message);
    });

    // Subscribe events from mqtt under specific care unit
    mqttClient.subscribe(
      topicEvent + "/" + store.scStation.careUnitId + "/#",
      (error, res) => {
        if (error) {
          console.log("Subscribe to event topics error", error);
          return;
        }
        console.log("Subscribe to event topics res", res);
      },
    );
    // Subscribe shadow from mqtt
    mqttClient.subscribe(topicShadow, (error, res) => {
      if (error) {
        console.log("Subscribe to shadow topics error", error);
        return;
      }
      console.log("Subscribe to shadow topics res", res);
    });
  }

  function setMqttEvents(topic, message) {
    try {
      // Ignore unused timing event 200501 and 200402 to improve performance
      if (!topic.includes("200402") && !topic.includes("200501")) {
        let newEvent = JSON.parse(message);
        setEvent((existEvents) =>
          updateBoxEvents(existEvents, newEvent, topic),
        );
      }
    } catch (error) {
      // ignore malformed
    }
  }

  function updateBoxEvents(existEvents, newEvent, topic) {
    // Copy previous boxes events
    let updatedBoxEvents = JSON.parse(JSON.stringify(existEvents));

    // initialize event if boxid not found
    if (
      !(newEvent.boxId in updatedBoxEvents) &&
      newEvent.careUnitId === store.scStation.careUnitId
    )
      updatedBoxEvents[newEvent.boxId] = {
        major: {},
        timing: {},
        generationSettings: {},
      };

    if (topic.includes(eventTypes.major)) {
      updatedBoxEvents[newEvent.boxId][eventTypes.major] = newEvent;
    } else if (topic.includes(shadowTypes.generationSettings)) {
      if (newEvent.careUnitId === store.scStation.careUnitId) {
        if (
          newEvent.boxId in existEvents &&
          Object.keys(existEvents[newEvent.boxId].generationSettings).length !==
            0
        )
          setnewGenerationSetting(newEvent); // For reload boxes when patient checked in or out
        updatedBoxEvents[newEvent.boxId][shadowTypes.generationSettings] =
          newEvent;
      }
    } else if (topic.includes(eventTypes.timing)) {
      updatedBoxEvents[newEvent.boxId][eventTypes.timing] = {
        ...updatedBoxEvents[newEvent.boxId][eventTypes.timing],
        ...{ [newEvent["eCode"].replace("s", "")]: newEvent },
        // Silence event should only save "s" to the content, otherwise it may exist two types of same events.
        // ex: 200601s -> { timing: { 200601: {eCode: 200601s, ...} } }
      };
    }
    return updatedBoxEvents;
  }
};

export default MqttProvider;
